"use client";

import RTHMDirectLogo from "@/components/SVG/Logos/RTHM";
import { ChatSidebarToggle } from "@/components/chat-sidebar-toggle";
import { OrdersBackToggle } from "@/components/orders-back-toggle";
import { Button } from "@/components/ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
    Sheet,
    SheetContent,
    SheetTitle,
    SheetTrigger,
} from "@/components/ui/sheet";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import {
    AcademicCapIcon,
    BeakerIcon,
    ChartBarSquareIcon,
    ChatBubbleLeftRightIcon,
    ChevronDownIcon,
    DocumentTextIcon,
    PowerIcon,
    QuestionMarkCircleIcon,
    ShoppingBagIcon,
    UserIcon,
    UsersIcon,
} from "@heroicons/react/24/outline";
import {
    Database,
    HomeIcon,
    Menu,
    MoonIcon,
    SunIcon,
    User,
    X,
} from "lucide-react";
import { Session } from "next-auth";
import { signOut } from "next-auth/react";
import { useTheme } from "next-themes";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import React, { useEffect, useState } from "react";

interface IntelligenceHeaderClientProps {
    session: Session | null;
}

interface NavItem {
    name: string;
    href: string;
    icon: React.ComponentType<{ className?: string }>;
    requiresAuth: boolean;
}

export default function IntelligenceHeaderClient({
    session,
}: IntelligenceHeaderClientProps) {
    const isMobile = useIsMobile();
    const router = useRouter();
    const [mounted, setMounted] = useState(false);
    const { theme, setTheme } = useTheme();
    const pathname = usePathname();
    const [isHeaderReady, setIsHeaderReady] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [showScrollIndicator, setShowScrollIndicator] = useState(false);
    const scrollContainerRef = React.useRef<HTMLDivElement>(null);

    const navigationItems: NavItem[] = [
        {
            name: "Home",
            href: "/dashboard",
            icon: HomeIcon,
            requiresAuth: true,
        },
        {
            name: "Chat",
            href: "/chat",
            icon: ChatBubbleLeftRightIcon,
            requiresAuth: true,
        },
        {
            name: "Orders",
            href: "/orders",
            icon: ChartBarSquareIcon,
            requiresAuth: true,
        },
        {
            name: "Data",
            href: "/my-data",
            icon: Database,
            requiresAuth: true,
        },
        {
            name: "Treatments",
            href: "/my-treatments",
            icon: BeakerIcon,
            requiresAuth: true,
        },
        {
            name: "Services",
            href: "/services",
            icon: ShoppingBagIcon,
            requiresAuth: true,
        },
        {
            name: "Intelligence",
            href: "/intelligence",
            icon: AcademicCapIcon,
            requiresAuth: false,
        },
        {
            name: "Treatments",
            href: "/treatments",
            icon: BeakerIcon,
            requiresAuth: false,
        },
        {
            name: "Team",
            href: "/team",
            icon: UsersIcon,
            requiresAuth: false,
        },
        {
            name: "Resources",
            href: "/resources",
            icon: DocumentTextIcon,
            requiresAuth: false,
        },
        {
            name: "Support",
            href: "/support",
            icon: QuestionMarkCircleIcon,
            requiresAuth: false,
        },
    ];

    // Filter navigation items based on auth status
    // For logged out users, show public Treatments and Support but not authenticated Treatments
    // For logged in users, exclude Support from main nav (it's in the profile dropdown)
    const visibleNavItems = navigationItems.filter((item) => {
        if (session?.user) {
            // For logged in users, exclude Support (it's in profile dropdown) and public Treatments
            if (item.name === "Support") return false;
            if (item.name === "Treatments" && item.href === "/treatments")
                return false;
            if (item.name === "Intelligence" && item.href === "/intelligence")
                return false;
            if (item.name === "Resources" && item.href === "/resources")
                return false;
            return !item.requiresAuth || session?.user;
        } else {
            return !item.requiresAuth;
        }
    });

    useEffect(() => {
        setMounted(true);
        if (isMobile !== null) {
            setIsHeaderReady(true);
        }
    }, [isMobile]);

    useEffect(() => {
        // Close mobile menu on route change
        setIsMobileMenuOpen(false);
    }, [pathname]);

    useEffect(() => {
        const checkScroll = () => {
            if (scrollContainerRef.current) {
                const { scrollTop, scrollHeight, clientHeight } =
                    scrollContainerRef.current;
                const hasMoreContent = scrollHeight > clientHeight;
                const isAtBottom =
                    scrollTop + clientHeight >= scrollHeight - 10; // 10px threshold
                setShowScrollIndicator(hasMoreContent && !isAtBottom);
            }
        };

        // Check initially when menu opens
        if (isMobileMenuOpen) {
            setTimeout(checkScroll, 100); // Small delay to ensure DOM is ready
        }

        const scrollContainer = scrollContainerRef.current;
        if (scrollContainer) {
            scrollContainer.addEventListener("scroll", checkScroll);
            return () =>
                scrollContainer.removeEventListener("scroll", checkScroll);
        }
    }, [isMobileMenuOpen, visibleNavItems]);

    const logOut = async () => {
        document.cookie =
            "visitorId-discover=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
        document.cookie =
            "visitorId-intelligence=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
        localStorage.removeItem("visitorId-discover");
        localStorage.removeItem("visitorId-intelligence");
        localStorage.removeItem("healthBannerCompletedDismissed");
        localStorage.removeItem("cachedMetriportStatus");
        await signOut({ redirectTo: "/", redirect: true });
    };

    const isActive = (path: string) => {
        return pathname.startsWith(path);
    };

    const getTourId = (item: NavItem) => {
        switch (item.href) {
            case "/dashboard":
                return "header-dashboard-link";
            case "/chat":
                return "header-ai-chat-link";
            case "/orders":
                return "header-orders-link";
            case "/my-data":
                return "header-my-data-link";
            case "/my-treatments":
                return "header-treatments-link";
            case "/services":
                return "header-services-link";
            default:
                return undefined;
        }
    };

    const MobileNavItem = ({ item }: { item: NavItem }) => (
        <Link
            href={item.href}
            data-tour-id={getTourId(item)}
            className={cn(
                "flex items-center space-x-3 px-4 py-2 rounded-lg transition-colors",
                isActive(item.href)
                    ? "bg-neutral-200 dark:bg-neutral-700 text-neutral-900 dark:text-white font-medium"
                    : "text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800",
            )}
            onClick={() => setIsMobileMenuOpen(false)}
        >
            <item.icon className="h-5 w-5" />
            <span className="text-base">{item.name}</span>
        </Link>
    );

    const DesktopNavItem = ({ item }: { item: NavItem }) => (
        <Link
            href={item.href}
            data-tour-id={getTourId(item)}
            className={cn(
                "relative px-1 py-2 text-sm font-medium transition-all duration-200",
                isActive(item.href)
                    ? "text-neutral-900 dark:text-white"
                    : "text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white",
            )}
        >
            {item.name}
            {isActive(item.href) && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-neutral-900 dark:bg-white rounded-full" />
            )}
        </Link>
    );

    const renderProfileMenu = () => (
        <>
            {session?.user ? (
                <DropdownMenu modal={false}>
                    <DropdownMenuTrigger asChild>
                        <Button
                            variant="ghost"
                            data-tour-id="header-more-button"
                            className={cn(
                                "relative z-10 focus:outline-none focus-visible:ring-0 focus:ring-0",
                                isMobile
                                    ? "p-2"
                                    : "text-sm px-2 py-1.5 flex items-center gap-1 text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white transition-colors",
                            )}
                        >
                            {isMobile ? (
                                <UserIcon className="h-5 w-5" />
                            ) : (
                                <>
                                    <span>More</span>
                                    <ChevronDownIcon className="h-4 w-4 opacity-70" />
                                </>
                            )}
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                        align="end"
                        className="w-56 mt-1 border border-border/80 shadow-lg rounded-lg overflow-hidden z-[200]"
                    >
                        <DropdownMenuItem
                            onClick={() =>
                                setTheme(theme === "dark" ? "light" : "dark")
                            }
                        >
                            {mounted && theme === "dark" ? (
                                <div className="flex items-center">
                                    <SunIcon className="w-5 h-5 mr-6 text-yellow-400" />{" "}
                                    Light Mode
                                </div>
                            ) : (
                                <div className="flex items-center">
                                    <MoonIcon className="w-5 h-5 mr-6 text-foreground/80" />{" "}
                                    Dark Mode
                                </div>
                            )}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem asChild>
                            <Link
                                href="/profile"
                                className="flex items-center cursor-pointer"
                            >
                                <UserIcon className="w-5 h-5 mr-4 text-foreground/80" />{" "}
                                Profile
                            </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                            <Link
                                href="/support"
                                className="flex items-center cursor-pointer"
                            >
                                <QuestionMarkCircleIcon className="w-5 h-5 mr-4 text-foreground/80" />{" "}
                                Support
                            </Link>
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                            onClick={logOut}
                            className="cursor-pointer"
                        >
                            <PowerIcon className="w-5 h-5 mr-4 text-foreground/80" />{" "}
                            Sign Out
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            ) : (
                <Button
                    variant="ghost"
                    size="sm"
                    className={cn(
                        "rounded-full transition-colors",
                        isMobile ? "p-2" : "px-3 py-1",
                    )}
                    onClick={() => {
                        router.push(`/sign-in`);
                    }}
                >
                    <UserIcon className="h-5 w-5" />
                    {!isMobile && <span className="ml-2 text-sm">Log In</span>}
                </Button>
            )}
        </>
    );

    if (!isHeaderReady) {
        return (
            <div className="h-14 bg-background/80 backdrop-blur-md border-b border-border/60">
                {/* Empty placeholder with same height as header to prevent layout shift */}
            </div>
        );
    }

    if (isMobile) {
        return (
            <>
                <header className="fixed top-0 left-0 right-0 z-50 bg-background backdrop-blur-md border-b border-border/60">
                    <div className="flex items-center justify-between px-4 py-3">
                        {/* Placeholder div for left side */}

                        {pathname.startsWith("/chat") ? (
                            <div className="z-50">
                                <ChatSidebarToggle user={session?.user} />
                            </div>
                        ) : pathname.startsWith("/orders/") ? (
                            <div className="z-50">
                                <OrdersBackToggle />
                            </div>
                        ) : (
                            <div className="w-10 h-10"></div>
                        )}

                        {/* Logo */}
                        <Link href="/" aria-label="Home">
                            <RTHMDirectLogo className="h-6 w-auto" />
                        </Link>

                        {/* Hamburger Menu */}
                        <Sheet
                            open={isMobileMenuOpen}
                            onOpenChange={setIsMobileMenuOpen}
                        >
                            <SheetTrigger asChild>
                                <Button variant="ghost" size="icon">
                                    <Menu className="h-5 w-5" />
                                    <span className="sr-only">Toggle menu</span>
                                </Button>
                            </SheetTrigger>
                            <SheetTitle className="sr-only">
                                Mobile Menu
                            </SheetTitle>
                            <SheetContent
                                side="right"
                                className="w-[280px] p-0 [&>button]:hidden flex flex-col h-full"
                            >
                                <div className="flex items-center justify-between px-4 py-4 border-b flex-shrink-0">
                                    <Link
                                        href="/"
                                        aria-label="Home"
                                        onClick={() =>
                                            setIsMobileMenuOpen(false)
                                        }
                                    >
                                        <RTHMDirectLogo className="h-6 w-auto" />
                                    </Link>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() =>
                                            setIsMobileMenuOpen(false)
                                        }
                                    >
                                        <X className="h-5 w-5" />
                                        <span className="sr-only">
                                            Close menu
                                        </span>
                                    </Button>
                                </div>

                                {/* Scrollable navigation section with indicator */}
                                <div className="flex-1 relative overflow-hidden min-h-0">
                                    <div
                                        className="h-full overflow-y-auto max-h-[50vh]"
                                        ref={scrollContainerRef}
                                    >
                                        <nav className="flex flex-col gap-1 p-2">
                                            {visibleNavItems.map((item) => (
                                                <MobileNavItem
                                                    key={item.href}
                                                    item={item}
                                                />
                                            ))}
                                        </nav>
                                    </div>

                                    {/* Scroll indicator - overlaid at bottom */}
                                    {showScrollIndicator && (
                                        <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-background to-transparent pointer-events-none flex items-end justify-center pb-1">
                                            <ChevronDownIcon className="h-4 w-4 text-neutral-400 dark:text-neutral-500 animate-pulse" />
                                        </div>
                                    )}
                                </div>

                                {/* Fixed bottom sections */}
                                <div className="flex-shrink-0">
                                    {/* Profile Menu in Mobile */}
                                    {session?.user && (
                                        <div className="border-t px-2 py-2">
                                            <div className="flex flex-col gap-1">
                                                <Link
                                                    href="/profile"
                                                    className="flex items-center space-x-3 px-4 py-2 rounded-lg text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
                                                    onClick={() =>
                                                        setIsMobileMenuOpen(
                                                            false,
                                                        )
                                                    }
                                                >
                                                    <UserIcon className="h-5 w-5" />
                                                    <span className="text-base">
                                                        Profile
                                                    </span>
                                                </Link>
                                                <Link
                                                    href="/support"
                                                    className="flex items-center space-x-3 px-4 py-2 rounded-lg text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
                                                    onClick={() =>
                                                        setIsMobileMenuOpen(
                                                            false,
                                                        )
                                                    }
                                                >
                                                    <QuestionMarkCircleIcon className="h-5 w-5" />
                                                    <span className="text-base">
                                                        Support
                                                    </span>
                                                </Link>
                                                {/* Theme Toggle in Profile Section */}
                                                {mounted && (
                                                    <button
                                                        onClick={() =>
                                                            setTheme(
                                                                theme === "dark"
                                                                    ? "light"
                                                                    : "dark",
                                                            )
                                                        }
                                                        className="flex items-center space-x-3 w-full px-4 py-2 rounded-lg text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
                                                    >
                                                        {theme === "dark" ? (
                                                            <>
                                                                <SunIcon className="h-5 w-5 text-yellow-400" />
                                                                <span className="text-base">
                                                                    Light Mode
                                                                </span>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <MoonIcon className="h-5 w-5" />
                                                                <span className="text-base">
                                                                    Dark Mode
                                                                </span>
                                                            </>
                                                        )}
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => {
                                                        setIsMobileMenuOpen(
                                                            false,
                                                        );
                                                        logOut();
                                                    }}
                                                    className="flex items-center space-x-3 w-full px-4 py-2 rounded-lg text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors text-left"
                                                >
                                                    <PowerIcon className="h-5 w-5" />
                                                    <span className="text-base">
                                                        Sign Out
                                                    </span>
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    {/* Theme Toggle for non-authenticated users */}
                                    {!session?.user && mounted && (
                                        <div className="border-t px-4">
                                            <button
                                                onClick={() =>
                                                    setTheme(
                                                        theme === "dark"
                                                            ? "light"
                                                            : "dark",
                                                    )
                                                }
                                                className="flex items-center space-x-3 w-full px-4 py-3 rounded-lg text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
                                            >
                                                {theme === "dark" ? (
                                                    <>
                                                        <SunIcon className="h-5 w-5 text-yellow-400" />
                                                        <span className="text-base">
                                                            Light Mode
                                                        </span>
                                                    </>
                                                ) : (
                                                    <>
                                                        <MoonIcon className="h-5 w-5" />
                                                        <span className="text-base">
                                                            Dark Mode
                                                        </span>
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    )}
                                    {!session?.user && (
                                        <div className="px-4">
                                            <button
                                                className="flex items-center space-x-3 w-full px-4 py-3 rounded-lg text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
                                                onClick={() => {
                                                    setIsMobileMenuOpen(false);
                                                    router.push(`/sign-in`);
                                                }}
                                            >
                                                <>
                                                    <UserIcon className="h-5 w-5" />
                                                    <span className="text-base">
                                                        Log In
                                                    </span>
                                                </>
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </SheetContent>
                        </Sheet>
                    </div>
                </header>
                {/* Spacer to prevent content from going under fixed header */}
                <div className="h-14" />
            </>
        );
    }

    return (
        <>
            <header className="flex fixed top-0 left-0 right-0 z-[50] bg-background/98 backdrop-blur-md border-b border-border/60 shadow-lg">
                <div className="w-full mx-auto px-4 lg:px-6 xl:px-8 py-3 relative">
                    {/* Absolutely positioned left section */}
                    <div className="absolute left-4 lg:left-6 xl:left-8 top-1/2 transform -translate-y-1/2 flex items-center z-10">
                        <Link href="/" aria-label="Home">
                            <RTHMDirectLogo className="h-6 w-auto" />
                        </Link>
                        {pathname.startsWith("/chat") && (
                            <div className="ml-4">
                                <ChatSidebarToggle user={session?.user} />
                            </div>
                        )}
                        {pathname.startsWith("/orders/") && (
                            <div className="ml-4">
                                <OrdersBackToggle />
                            </div>
                        )}
                    </div>

                    {/* Center section with absolute positioning to ensure it stays centered */}
                    <div className="flex justify-center items-center h-full">
                        <nav className="flex items-center space-x-6">
                            {visibleNavItems.map((item) => (
                                <DesktopNavItem key={item.href} item={item} />
                            ))}
                        </nav>
                    </div>

                    {/* Absolutely positioned right section */}
                    <div className="absolute right-4 lg:right-6 xl:right-8 top-1/2 transform -translate-y-1/2 z-1000">
                        {renderProfileMenu()}
                    </div>
                </div>
            </header>
            {/* Spacer to prevent content from going under fixed header */}
            <div className="h-14" />
        </>
    );
}
